"use client"

import { Form } from "@/components/form"
import { SubmitButton } from "@/components/submit-button"
import { Translate } from "@/components/translate"
import { Button } from "@/components/ui/button"
import { CheckboxField } from "@/components/ui/checkbox-field"
import { Loader } from "@/components/ui/loader"
import { arrayCheckbox } from "@/config/consts"
import { useConfigParams } from "@/hooks/use-config-params"
import { useRecaptcha } from "@/hooks/use-recaptcha"
import { useCustomSearchParams } from "@/hooks/use-search-params"
import { shouldShowPreRegistrationSteps } from "@/lib/affiliation"
import { trackAnalyticsEvent } from "@/lib/analytics"
import { AuthType } from "@/lib/auth-type"
import { createRequiredField } from "@/lib/validations/required"
import {
  BaseLoginPayload,
  TloginErrorResponse,
  TloginResponse,
  useInfoLoginMutation,
  useLoginMutation,
  usePostLoginRedirectUrl,
} from "@/services"
import {
  TOAuthLoginResponse,
  useOAuthLoginMutation,
} from "@/services/post-login-oauth"
import {
  TSsoLoginResponse,
  useSsoLoginMutation,
} from "@/services/post-login-sso"
import { usePersonalDataStore } from "@/store/personal-data-store"
import { DevTool } from "@hookform/devtools"
import { zodResolver } from "@hookform/resolvers/zod"
import { isErr, isOk, Result } from "@pacote/result"
import Cookies from "js-cookie"
import dynamic from "next/dynamic"
import { useRouter } from "next/navigation"
import { useEffect } from "react"
import { useController, useForm } from "react-hook-form"
import { z } from "zod"
import {
  LoginFormPasswordInput,
  PASSWORD_REQUIRED_ERROR,
} from "./login-form-password-input"
import {
  LoginFormUsernameInput,
  USERNAME_REQUIRED_ERROR,
} from "./login-form-username-input"
import { recoveryPasswordParam } from "./password-recovery-dialog/password-recovery-dialog.config"
import { verifyContacts } from "./post-login-dialog/post-login-dialog.config"

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const LazyAccountBlockedModal = dynamic(
  () => import("./account-blocked-dialog"),
)
const LazyExpiringPasswordDialog = dynamic(
  () => import("./expiring-password-dialog"),
)
const LazyPostLoginDialog = dynamic(
  () => import("./post-login-dialog/post-login-dialog"),
)
const LazyPasswordRecoveryDialog = dynamic(
  () => import("./password-recovery-dialog/password-recovery-dialog"),
)
const LazyMultiModalDialog = dynamic(() => import("./multimodal-dialog"))

const FormSchema = z.object({
  usernameEtc: createRequiredField(USERNAME_REQUIRED_ERROR),
  password: createRequiredField(PASSWORD_REQUIRED_ERROR),
  rememberUsername: z.boolean(),
})

export type LoginFormValues = z.infer<typeof FormSchema>

/* -------------------------------------------------------------------------------------------------
 * component
 * -----------------------------------------------------------------------------------------------*/

export function LoginForm() {
  /*--------------------hooks--------------------*/
  const [searchParams, setSearchParams] = useCustomSearchParams()
  const { channel, authType } = useConfigParams()
  const router = useRouter()
  const { data: loginData, trigger: makeLogin } = useLoginMutation()
  const { trigger: makeInfoLogin } = useInfoLoginMutation()
  const getPostLoginRedirectUrl = usePostLoginRedirectUrl()
  const { data: oauthLoginData, trigger: makeOAuthLogin } =
    useOAuthLoginMutation()
  const { data: ssoLoginData, trigger: makeSsoLogin } = useSsoLoginMutation()

  const { setPassword, setUsername } = usePersonalDataStore()

  const getRecaptchaToken = useRecaptcha("LOGIN")

  /*--------------------variables config--------------------*/

  const showRememberUsernameField = arrayCheckbox.includes(channel)
  const canShowDialogs =
    (!!loginData && isOk(loginData)) ||
    (!!oauthLoginData && isOk(oauthLoginData)) ||
    (!!ssoLoginData && isOk(ssoLoginData))
  /*--------------------helpers--------------------*/

  const setFormError = (message: string) => {
    form.setError("usernameEtc", {
      type: "custom",
      message: `Errors.emptyError`,
    })
    form.setError("password", {
      type: "custom",
      message,
    })
  }

  const getLoginResponse = async (
    username: string,
    password: string,
  ): Promise<
    Result<
      TloginResponse | TOAuthLoginResponse | TSsoLoginResponse,
      TloginErrorResponse
    >
  > => {
    const { token: recaptchaToken, platform } = await getRecaptchaToken()

    const baseLoginPayload: BaseLoginPayload = {
      username,
      password,
      channel,
      recaptchaToken,
    }

    switch (authType) {
      case AuthType.OAuth:
        return await makeOAuthLogin(baseLoginPayload)
      case AuthType.Sso:
        return await makeSsoLogin({ ...baseLoginPayload, platform })
      default:
        return await makeLogin(baseLoginPayload)
    }
  }

  /*--------------------form config--------------------*/

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: {
      usernameEtc: Cookies.get("username") ?? "",
      password: "",
      rememberUsername: false,
    },
  })

  const rememberUsernameField = useController({
    name: "rememberUsername",
    control: form.control,
  }).field

  /*--------------------submit handler--------------------*/
  const onSubmit = form.handleSubmit(async (data) => {
    trackAnalyticsEvent({
      event: "link_click",
      event_name: "nav_click",
      conversion_pagename: "login",
      position: "login",
      cta_label: "accedi",
    })

    setSearchParams((prevParams) => {
      prevParams.delete("errorBanner", "serviceError")
      return prevParams
    })

    if (!isEmail(data.usernameEtc)) {
      setUsername(data.usernameEtc)
      Cookies.set("username", data.usernameEtc, { expires: 30 })
    }
    setPassword(data.password)

    const response = await getLoginResponse(data.usernameEtc, data.password)

    if (isErr(response)) {
      switch (response.value.type) {
        case "SOGEI":
          setSearchParams((prevParams) => {
            prevParams.set("errorBanner", "sogei")
            return prevParams
          })
          return
        case "CREDENTIALS":
          setFormError(`Errors.invalidCredentials.${response.value.counter}`)
          return
        case "ERROR_RECAPTCHA":
          setFormError("Errors.recaptcha")
          return
        default:
          setSearchParams((prevParams) => {
            prevParams.set("errorBanner", "serviceError")
            return prevParams
          })
          return
      }
    }

    if (
      ("token" in response.value && response.value.token) ||
      ("jwt" in response.value && response.value.jwt)
    ) {
      const infoLoginResponse = await makeInfoLogin()

      if (
        isOk(infoLoginResponse) &&
        shouldShowPreRegistrationSteps(infoLoginResponse.value)
      ) {
        router.push(`/crea-password?${searchParams.toString()}`)
        return
      }
    }

    const { postLoginAction, loginBlockedType } = response.value

    switch (postLoginAction.type) {
      case "URL_REDIRECT":
        if (response.value.contractSettings === 0) {
          router.push(`/migrazione?${searchParams.toString()}`)
        } else {
          const url = getPostLoginRedirectUrl(response.value)
          window.location.href = url
        }
        return
      case "ACCOUNT_BLOCKED":
        if (loginBlockedType === "SUSPEND") setFormError(`Errors.invalidData`)
        setSearchParams((prevParams) => {
          prevParams.set("accountBlocked", "open")
          return prevParams
        })
        return
      case "QUESTIONARIO":
        router.push("/Questionario?" + searchParams.toString())
        return
      case "VERIFY_CONTACTS":
        setSearchParams((prevParams) => {
          prevParams.set(verifyContacts, "open")
          return prevParams
        })
        return
      case "PASSWORD_EXPIRING":
        setSearchParams((prevParams) => {
          prevParams.set("passwordExpiring", "open")
          return prevParams
        })
        return
      default:
        if (postLoginAction.showListModals) {
          setSearchParams((prevParams) => {
            prevParams.set("listModal", "open")
            return prevParams
          })
        }
    }
  })

  /*--------------------effects--------------------*/
  //quando cambiamo il valore di un campo a seguito di errore " invalidCredentials "
  //dobbiamo resettare gli errori per far si che venga rimosso lo stato di errore anche dal campo username
  //E' un pò bruttino ma è il modo più semplice per farlo
  useEffect(() => {
    const subscription = form.watch(() => {
      if (form.formState.errors.usernameEtc?.message === "Errors.emptyError")
        return form.clearErrors()
    })
    return () => subscription.unsubscribe()
  }, [form])

  /*--------------------event handlers --------------------*/
  const handlePasswordBlur = () => {
    const passwordValue = form.getValues("password")

    if (!passwordValue) {
      form.setError("password", {
        type: "custom",
        message: PASSWORD_REQUIRED_ERROR,
      })
    }
  }

  return (
    <>
      <Form {...form}>
        {form.formState.isSubmitting && <Loader />}
        <form onSubmit={onSubmit} className="flex w-full flex-col gap-6">
          {/*--------------------input fields--------------------*/}
          <LoginFormUsernameInput />

          <LoginFormPasswordInput
            name="password"
            control={form.control}
            onBlur={handlePasswordBlur}
          />

          <div>
            {/*--------------------remember username field--------------------*/}
            {showRememberUsernameField && (
              <div className="flex gap-2.5">
                <CheckboxField {...rememberUsernameField} />
                <label
                  htmlFor="rememberUsername"
                  className="cursor-p text-field-text text-[0.75rem] font-light"
                >
                  <Translate
                    i18nKey={"LoginPage.LoginForm.rememberUsernameFieldLabel"}
                  />
                </label>
              </div>
            )}
            <Button
              onClick={() => {
                setSearchParams((prevParams) => {
                  prevParams.set(recoveryPasswordParam, "true")
                  return prevParams
                })
              }}
              variant="ghost"
              className="place-self-start pl-0 underline"
            >
              <Translate i18nKey={"LoginPage.forgotPassword"} />
            </Button>
          </div>
          {/*--------------------submit button--------------------*/}
          <SubmitButton variant="secondary">
            <Translate i18nKey={"LoginPage.LoginForm.submitBtn"} />
          </SubmitButton>
        </form>
      </Form>
      {canShowDialogs && searchParams.get("accountBlocked") === "open" && (
        <LazyAccountBlockedModal />
      )}
      {canShowDialogs && searchParams.get("passwordExpiring") === "open" && (
        <LazyExpiringPasswordDialog />
      )}
      {canShowDialogs && searchParams.get(verifyContacts) === "open" && (
        <LazyPostLoginDialog />
      )}
      {canShowDialogs && searchParams.get("listModal") === "open" && (
        <LazyMultiModalDialog />
      )}
      {searchParams.get(recoveryPasswordParam) === "true" && (
        <LazyPasswordRecoveryDialog />
      )}

      <DevTool control={form.control} />
    </>
  )
}

function isEmail(value: string) {
  return z.string().email().safeParse(value).success
}
